import React, { useState } from 'react';
import styled from 'styled-components';
import CmsPopup from '../../../../components/CmsPopup';
import { AppTextField } from '../../../../customComponents/AppTextField';
import CustomTiptapEditor from '../../../../components/Editor/CustomTiptapEditor';
import { CmsPopupBtnType } from '../../../../components/CmsPopupBtn';
import ProfileImageAdd from '../../../../components/ProfileImageAdd';

// 경매사 폼 데이터 타입 (서버 스키마에 맞춰 수정)
export interface AuctioneerFormData {
  name: string; // 경매사 이름
  description: string;
  profilePic: string; // 프로필 이미지 (서버에서는 profilePic)
  term: string; // 약관 (서버에서는 term)
}

interface AuctioneerFormPopupProps {
  isOpen: boolean;
  isEditMode: boolean;
  isSaving?: boolean;
  formData: AuctioneerFormData;
  formErrors: Partial<AuctioneerFormData>;
  onClose: () => void;
  onSave: () => Promise<void> | void;
  onDelete?: () => void;
  onInputChange: (field: keyof AuctioneerFormData, value: string) => void;
  onImageUpload: (file: File) => void;
}

const AuctioneerFormPopup: React.FC<AuctioneerFormPopupProps> = ({
  isOpen,
  isEditMode,
  isSaving = false,
  formData,
  formErrors,
  onClose,
  onSave,
  onDelete,
  onInputChange,
  onImageUpload,
}) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 이미지 업로드 성공 콜백
  const handleImageUploaded = (fileName: string) => {
    onInputChange('profilePic', fileName);
  };

  // 이미지 삭제 핸들러
  const handleImageDelete = () => {
    onInputChange('profilePic', '');
  };

  // 에디터 내용 변경 핸들러
  const handleEditorChange = (content: string) => {
    onInputChange('term', content);
  };

  const handleSaveClick = async () => {
    if (isSaving || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave();
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled = isSaving || isSubmitting;

  const rightButtons = [
    {
      label: isSubmitDisabled ? '저장 중...' : '저장',
      onClick: handleSaveClick,
      type: CmsPopupBtnType.PRIMARY,
      disabled: isSubmitDisabled,
    },
    {
      label: '닫기',
      onClick: onClose,
      type: CmsPopupBtnType.SECONDARY,
    },
  ];

  const leftButtons = isEditMode && onDelete ? [
    {
      label: '삭제',
      onClick: () => setIsDeleteModalOpen(true),
      type: CmsPopupBtnType.PRIMARY,
      disabled: isSubmitDisabled,
    },
  ] : undefined;

  // 삭제 확인 핸들러
  const handleDeleteConfirm = () => {
    setIsDeleteModalOpen(false);
    if (onDelete) {
      onDelete();
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
  };

  return (
    <CmsPopup
      isOpen={isOpen}
      onClose={onClose}
      isWide={true}
      showRequiredMark={true}
      rightButtons={rightButtons}
      leftButtons={leftButtons}
    >
      <Wrapper>
        <Container>
          <LeftSection>
          <SectionTitleRow>
            <SectionTitle>경매사 등록상세</SectionTitle>
            <RequiredIndicator>*필수항목</RequiredIndicator>
          </SectionTitleRow>
          <FormContainer>
            <SubSectionTitle>경매사정보</SubSectionTitle>
            
            <ImageUploadField>
              <ImageLabel>*로고 이미지 등록</ImageLabel>
              <ImageFieldContainer>
                <ProfileImageAdd
                  initialImageFileName={formData.profilePic}
                  size={160}
                  onImageUploaded={handleImageUploaded}
                  onImageDelete={handleImageDelete}
                />
              </ImageFieldContainer>
              <ImageNotice>* 1장의 이미지만 첨부 가능합니다.</ImageNotice>
              <FileFormatNotice>WEBP, PNG, JPG, JPEG 파일만 가능</FileFormatNotice>
              {formErrors.profilePic && <ErrorText>{formErrors.profilePic}</ErrorText>}
            </ImageUploadField>
            
            <AppTextField
              value={formData.name}
              onChange={(e) => onInputChange('name', e.target.value)}
              label="*경매사 이름"
              placeholder="경매사 이름을 입력하세요"
              errorMessage={formErrors.name}
            />

            <AppTextField
              value={formData.description}
              onChange={(e) => onInputChange('description', e.target.value)}
              label="경매사 소개글"
              placeholder="경매사 소개글을 입력하세요"
              errorMessage={formErrors.description}
              multiline
              minLines={4}
              height="200px"
            />
          </FormContainer>
        </LeftSection>

        <RightSection>
          <SectionTitle>경매사 약관관리</SectionTitle>
          <EditorContainer>
            <CustomTiptapEditor
              key={`term-${formData.name}-${isOpen}`}
              content={formData.term}
              onChange={handleEditorChange}
              placeholder=""
              enableImageUpload={false}
              height={'673px'}
            />
          </EditorContainer>
        </RightSection>
      </Container>
      </Wrapper>
      
      {/* 삭제 확인 다이얼로그 */}
      {isDeleteModalOpen && (
        <DeleteModalOverlay onClick={handleDeleteCancel}>
          <DeleteModalContainer onClick={(e) => e.stopPropagation()}>
            <DeleteModalTitle>경매사 삭제</DeleteModalTitle>
            <DeleteModalMessage>
              경매사를 삭제하시겠습니까?{'\n'}
              삭제된 데이터는 복구할 수 없습니다.
            </DeleteModalMessage>
            <DeleteModalButtons>
              <DeleteModalButton onClick={handleDeleteCancel} variant="cancel">
                취소
              </DeleteModalButton>
              <DeleteModalButton onClick={handleDeleteConfirm} variant="delete">
                삭제
              </DeleteModalButton>
            </DeleteModalButtons>
          </DeleteModalContainer>
        </DeleteModalOverlay>
      )}
    </CmsPopup>
  );
};

// Styled Components
const Wrapper = styled.div`
  padding-top: 20px;
  height: 100%;
`;

const Container = styled.div`
  display: flex;
  gap: 20px;
  height: 100%;
`;

const LeftSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const RightSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const SectionTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e9ecef;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  text-align: left;
`;

const RequiredIndicator = styled.span`
  font-size: 14px;
  color: #dc3545;
  font-weight: 500;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ErrorText = styled.span`
  color: #dc3545;
  font-size: 12px;
`;

const SubSectionTitle = styled.h4`
  margin: 0 0 20px 0;
  font-size: 16px;
  font-weight: 600;
  color: #495057;
  text-align: left;
`;

const ImageUploadField = styled.div`
  position: relative;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 20px;
  background-color: #fafafa;
`;

const ImageLabel = styled.label`
  position: absolute;
  top: -10px;
  left: 12px;
  background-color: white;
  padding: 0 8px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
`;

const ImageFieldContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0;
`;

const ImageNotice = styled.div`
  color: #dc3545;
  font-size: 12px;
  text-align: left;
  margin-top: 10px;
`;

const FileFormatNotice = styled.div`
  color: #6c757d;
  font-size: 12px;
  text-align: left;
  margin-top: 5px;
`;

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
  
  .ProseMirror {
    min-height: 555px;
    padding: 15px;
    border: 1px solid #ddd;
    border-radius: 4px;
    
  }
`;

// 삭제 확인 다이얼로그 스타일 - Modal 컴포넌트 스타일 참고
const DeleteModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 20000; /* CmsPopup보다 높은 z-index */
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: auto;
`;

const DeleteModalContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  
  background-color: #FFFFFF;
  padding: 24px 14px 12px 14px;
  border-radius: 14px;
  width: 300px;
  max-width: calc(100vw - 32px);
  min-width: 280px;
  z-index: 20001;
  
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const DeleteModalTitle = styled.div`
  text-align: center;
  font-size: 16px;
  font-weight: 760;
  color: #000000;
  margin-bottom: 16px;
  white-space: pre-line;
`;

const DeleteModalMessage = styled.div`
  font-size: 16px;
  line-height: 1.5;
  color: #000000;
  text-align: center;
  margin-bottom: 24px;
  flex-grow: 1;
  white-space: pre-line;
`;

const DeleteModalButtons = styled.div`
  display: flex;
  gap: 12px;
`;

interface DeleteModalButtonProps {
  variant: 'cancel' | 'delete';
}

const DeleteModalButton = styled.button<DeleteModalButtonProps>`
  border: none;
  font-weight: 500;
  transition: all 0.2s;
  cursor: pointer;
  border-radius: 8px;
  font-size: 1rem;
  padding: 0.75rem;
  flex: 1;
  
  ${props => props.variant === 'cancel' ? `
    background-color: transparent;
    border: 1px solid #6c757d;
    color: #6c757d;
    
    &:hover {
      background-color: #6c757d1A;
      color: #6c757d;
    }
  ` : `
    background-color: #dc3545;
    color: #FFFFFF;
    
    &:hover {
      background-color: #c82333;
    }
  `}
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    border-color: transparent;
  }
`;

export default AuctioneerFormPopup;
